import { ENV } from '../config/envConfig';

/**
 * Fetches stock chart data from the backend API.
 *
 * @param {string} symbol - The stock ticker symbol to fetch
 * @returns {Promise<{price: Array, metrics: Object<string, Array>, shares: Array, netDebt: Array, epsQuarterly: Array, incomeStatement: {annual: Array, quarterly: Array, currency: (string|null)}}>} The chart data —
 *   `metrics` has one series per comparable metric (eps, revenue, ebitda, ebit, netIncome,
 *   fcf, ocf, sbc); `shares` and `netDebt` are annual-ish series used to derive Market Cap/Enterprise
 *   Value and per-share/total views client-side; `epsQuarterly` is chronological
 *   `[timestampMs, eps][]` quarterly EPS actuals (FX-converted like every other metric),
 *   used by the "Valuation vs. Subsequent Returns" card's realized-NTM-EPS backtest.
 *   `metrics.sbc` (stock-based compensation, added 2026-10-30) is REPORTED/TTM-ONLY — like
 *   fcf/ocf it has no analyst consensus on any FMP tier, but unlike them it has no implied-
 *   forward derivation either, so it carries no forward/estimate points at all; the "Adjust
 *   for SBC" toggle (`useStockData.js`'s `applySbcAdjustment`/`applyAdjustForSbc`) projects a
 *   forward value itself (last reported sbc/revenue ratio × forward revenue) rather than
 *   relying on a backend-supplied one.
 *   `estimatesDetail` carries, per metric, the analyst high/low band and contributor count
 *   at each forward FY estimate point (`{metric: [[timestampMs, {high, low, count}], ...]}`,
 *   FX-converted like `metrics`), keyed by the same timestamps as the estimate tail of
 *   `metrics`, for the main chart's hover tooltip; metrics with no analyst consensus
 *   (fcf/ocf/dividends/sbc) carry an empty array.
 *   `incomeStatement` carries curated income-statement line items for the Sankey card as
 *   `{annual, quarterly}` arrays (chronological, oldest-first; each element has `label`,
 *   `date`, `period`, `fiscalYear` plus the raw line-item amount fields) and a `currency`
 *   code — deliberately the company's REPORTING currency, NOT FX-converted onto the price axis
 *   like the other series, so the Sankey shows the statement as filed.
 * @throws {Error} On non-ok HTTP response or network failure
 */
export const fetchStockData = async (symbol) => {
  const url = `${ENV.BACKEND_URL}/${symbol}`;

  try {
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    if (error instanceof TypeError) {
      throw new Error(`Network error: ${error.message}`);
    }
    throw error;
  }
};

/**
 * Fetches symbol search suggestions from the backend search proxy.
 *
 * @param {string} searchTerm - The search query
 * @returns {Promise<Array>} Array of matching symbols
 * @throws {Error} On non-ok HTTP response or network failure — callers should
 *   distinguish this from a successful response with zero results.
 */
export const fetchSymbolSuggestions = async (searchTerm) => {
  try {
    const response = await fetch(
      `${ENV.BACKEND_URL}/search/${encodeURIComponent(searchTerm)}`
    );

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('API call failed:', error);
    throw error;
  }
};

/**
 * Resolves a company display name for a ticker via the search proxy.
 *
 * Used when the symbol was not chosen from the autocomplete dropdown — i.e. the
 * user typed a ticker directly, or picked a trending stock (which only carries
 * the symbol). Prefers an exact symbol match, then the top search result, and
 * falls back to the symbol itself if nothing resolves.
 *
 * @param {string} symbol - The ticker to resolve a name for
 * @returns {Promise<string>} The best-matching company name, or the symbol as fallback
 */
export const fetchCompanyName = async (symbol) => {
  const sym = (symbol || '').trim();
  if (!sym) return symbol;

  let results;
  try {
    results = await fetchSymbolSuggestions(sym);
  } catch {
    // fetchSymbolSuggestions throws on failure so search-UI callers can
    // distinguish "no results" from "search failed" — here we just want a
    // best-effort name, so fall back to the symbol itself.
    return sym;
  }

  if (Array.isArray(results) && results.length > 0) {
    const exact = results.find(
      (r) =>
        r &&
        typeof r.symbol === 'string' &&
        r.symbol.toUpperCase() === sym.toUpperCase() &&
        typeof r.name === 'string'
    );
    if (exact?.name) return exact.name;
    if (typeof results[0].name === 'string' && results[0].name) return results[0].name;
  }
  return sym;
};

/**
 * Fetches trending (most searched) stocks from the backend.
 *
 * @returns {Promise<Array<{symbol: string, count: number, prices: Array}>>}
 */
export const fetchTrending = async () => {
  try {
    const response = await fetch(`${ENV.BACKEND_URL}/trending`);
    if (!response.ok) return [];
    return await response.json();
  } catch {
    return [];
  }
};

/**
 * Fetches the Market Health payload from the backend.
 *
 * Same fail-soft, `status`-tagged convention as `fetchScreener` below (never
 * throws), but with a simpler success shape: on `status: 'ok'` the full
 * response body (`asOf`, `current`, `history`, `strategy` — see
 * market-health-spec.md §5) is spread alongside `status`. `status:
 * 'unavailable'` is the compute-not-yet-run case (HTTP 503 before the nightly
 * Lambda has written its first `signals/market-health.json.gz`, see spec
 * §7.2/§10), kept distinct from `status: 'error'` (any other non-ok
 * response, or a network failure) so the MarketHealth page can render a
 * "not available yet" message instead of a generic error.
 *
 * @returns {Promise<{
 *   status: 'ok'|'unavailable'|'error',
 *   asOf?: string,
 *   current?: {score: number, zone: string, dimensions: Object, raw: Object},
 *   history?: Array,
 *   strategy?: Object,
 * }>}
 */
let marketHealthCache = null;
let marketHealthPromise = null;

export const resetMarketHealthCache = () => {
  marketHealthCache = null;
  marketHealthPromise = null;
};

export const fetchMarketHealth = async () => {
  if (marketHealthCache) return marketHealthCache;
  if (marketHealthPromise) return marketHealthPromise;

  marketHealthPromise = (async () => {
    try {
      const response = await fetch(`${ENV.BACKEND_URL}/market-health`);
      if (response.status === 503) return { status: 'unavailable' };
      if (!response.ok) return { status: 'error' };
      const data = await response.json();
      const result = { status: 'ok', ...data };
      marketHealthCache = result;
      setTimeout(() => { marketHealthCache = null; }, 300_000);
      return result;
    } catch {
      return { status: 'error' };
    } finally {
      marketHealthPromise = null;
    }
  })();

  return marketHealthPromise;
};

/**
 * Fetches a page of the stock screener from the backend.
 *
 * Follows the same fail-soft convention as `fetchTrending` (never throws,
 * returns a safe empty shape on failure) but extends it with a `status`
 * field, because the Screener tab needs to tell three failure modes apart:
 * a real page of results, "no snapshot computed yet" (HTTP 503 — the
 * pipeline hasn't finished its first run), and any other failure. Callers
 * (see `useScreener.js`) render a distinct "still being prepared" message
 * for the 503 case rather than a generic error.
 *
 * @param {Object} [params] - Query parameters per the `/screener` API
 *   contract: `page`, `size`, `sort`, `dir`, `ranking`, `f.<metric>.min`,
 *   `f.<metric>.max`, `f.sector`, `f.country`, `q`. A value of `undefined`,
 *   `null` or `''` is omitted from the query string rather than sent as a
 *   literal empty filter.
 * @returns {Promise<{
 *   status: 'ok'|'unavailable'|'error',
 *   page: number, size: number, total: number, totalPages: number,
 *   asOf: string|null, sort: {key: string, dir: string}, rows: Array
 * }>}
 *   `status: 'ok'` carries the real response body's fields alongside
 *   `status`. `status: 'unavailable'` (HTTP 503) and `status: 'error'`
 *   (any other non-ok response, or a network failure) both carry an empty
 *   result shape — `total`/`totalPages` 0, `rows` [] — so a caller can
 *   render either without special-casing the shape, while still branching
 *   on `status` for the message shown.
 */
export const fetchScreener = async (params = {}) => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    searchParams.set(key, value);
  });
  const qs = searchParams.toString();
  const url = `${ENV.BACKEND_URL}/screener${qs ? `?${qs}` : ''}`;

  const emptyResult = (status) => ({
    status,
    page: params.page ?? 1,
    size: params.size ?? 25,
    total: 0,
    totalPages: 0,
    asOf: null,
    sort: { key: params.sort ?? 'mcap', dir: params.dir ?? 'desc' },
    rows: [],
  });

  try {
    const response = await fetch(url);

    if (response.status === 503) {
      return emptyResult('unavailable');
    }

    if (!response.ok) {
      return emptyResult('error');
    }

    const data = await response.json();
    return { status: 'ok', ...data };
  } catch {
    return emptyResult('error');
  }
};